import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import ReactGA from "react-ga4";
// TrackPageViews component for GA4 page tracking
function TrackPageViews() {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({
      hitType: "pageview",
      page: location.pathname + location.search,
    });

    console.log("GA pageview sent:", location.pathname);
  }, [location]);

  return null;
}

import { AuthProvider } from "./utils/AuthContext";
import { CartProvider } from "./utils/CartContext";
import { WishlistProvider } from "./utils/WishlistContext";
import { ToastProvider, useToast } from "./utils/ToastContext";
import { ToastContainer } from "./components/Toast";
import PrivateRoute from "./utils/PrivateRoute";
import AdminRoute from "./utils/AdminRoute";
import UserOnlyRoute from "./utils/UserOnlyRoute";
import Layout from "./components/Layout";
import "./styles/global.css";
import * as catalogApi from "./api/catalog";
import ProductCard from "./components/ProductCard";
import HeroCarousel from "./components/HeroCarousel";
import PromoSection from "./components/PromoSection";
// import Footer from "../components/Footer";
import { useAuth } from "./utils/AuthContext";
import { useCart } from "./utils/CartContext";
import * as api from "./api/auth";
import * as addressApi from "./api/address";
import * as orderApi from "./api/order";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import NotFoundPage from "./components/NotFoundPage";
import CategoriesPage from "./pages/Categories";
import Dashboard2 from "./pages/Dashboard2";
import BlogDetail from "./pages/BlogDetail";
// import Recommended from './pages/Recommended';

// import PublicOrderTracking from "./pages/PublicOrderTracking";

// Lazy load pages for faster initial load
const LoginSecurity = lazy(() => import("./pages/LoginSecurity"));
const Recommended = lazy(() => import("./pages/Recommended")); // ADD THIS LINE
const CategoryPage = lazy(() => import("./pages/CategoryPage"));
const Login = lazy(() => import("./pages/Auth/Login"));
const Register = lazy(() => import("./pages/Auth/Register"));
const Verify = lazy(() => import("./pages/Auth/Verify"));
const CompleteProfile = lazy(() => import("./pages/Auth/CompleteProfile"));
const ProductGrid = lazy(() => import("./pages/ProductGrid"));
const ProductListing = lazy(() => import("./pages/ProductListing"));
const ProductDetails = lazy(() => import("./pages/ProductDetails"));
const Cart = lazy(() => import("./pages/Cart"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const RecentOrders = lazy(() => import("./pages/RecentOrders"));
const CustomerSupport = lazy(() => import("./pages/CustomerSupport"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Dashboardprofile = lazy(() => import("./pages/Dashboardprofile"));
const Profile = lazy(() => import("./pages/Profile"));
const OrderDetails = lazy(() => import("./pages/OrderDetails"));
const CategoriesAdmin = lazy(() => import("./admin/Categories"));
const ProductsAdmin = lazy(() => import("./admin/Products"));
const CarouselAdmin = lazy(() => import("./admin/Carousel"));
const PromoAdmin = lazy(() => import("./admin/Promo"));
const OrdersAdmin = lazy(() => import("./admin/Orders"));
const Adminstration = lazy(() => import("./admin/Adminstration"));
const CustomersAdmin = lazy(() => import("./admin/Customers"));
const OutOfStock = lazy(() => import("./admin/OutOfStock"));
const StockAlerts = lazy(() => import("./admin/StockAlerts"));
const AdminProfile = lazy(() => import("./admin/Profile"));
const ChangePassword = lazy(() => import("./admin/ChangePassword"));
const OrderTrackingAdmin = lazy(() => import("./admin/OrderTracking"));
const PublicOrderTracking = lazy(() => import("./pages/PublicOrderTracking"));
const OffersAdmin = lazy(() => import("./admin/Offers"));
const Notification = lazy(() => import("./admin/Notification"));
const ActivityLog = lazy(() => import("./admin/ActivityLog"));
const AdminCustomerSupport = lazy(() => import("./admin/CustomerSupport"));
const BlogsAdmin = lazy(() => import("./admin/Blogs"));

// Reels components
const ReelsAdmin = lazy(() => import("./admin/Reels"));
const ReelFeed = lazy(() => import("./components/ReelFeed"));
const ShareableReelPage = lazy(() => import("./pages/ShareableReelPage"));
const BrandReelUpload = lazy(() => import("./components/BrandReelUpload"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const TermsAndConditions = lazy(() => import("./pages/TermsAndConditions"));
const FAQ = lazy(() => import("./pages/FAQ"));
const BlogDetails = lazy(() => import("./pages/BlogDetails"));

// Shop the Look admin (Video-based)
const ShopTheLookAdmin = lazy(() => import("./admin/ShopTheLook"));

// Shop the Look Image admin (Image-based)
const ShopTheLookImageAdmin = lazy(() => import("./admin/ShopTheLookImage"));

// Add Delivery Locations admin
const AddDeliveryLocations = lazy(() => import("./admin/AddDeliveryLocations"));

import InstallPWAButton from "./components/InstallPWAButton";

function Home() {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const [products, setProducts] = React.useState([]);

  // Redirect admin users to their dashboard
  React.useEffect(() => {
    if (user && user.role === "admin") {
      navigate("/admin");
    }
  }, [user, navigate]);

  const load = async () => {
    try {
      const items = await catalogApi.listProducts({ segment: "top-sellers" });
      setProducts(items || []);
    } catch (e) {
      console.error(e);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  const [cartDrawerOpen, setCartDrawerOpen] = React.useState(false);
  const onAddToCart = (p) => {
    if (!user) return navigate("/login");
    ReactGA.event({
      category: "Ecommerce",
      action: "Add_to_Cart",
      label: p._id,
    });
    addToCart(p);
    setCartDrawerOpen(true);
  };

  const buyNow = (p) => {
    if (!user) return navigate("/login");
    ReactGA.event({
      category: "Ecommerce",
      action: "Buy_Now",
      label: p._id,
    });
    addToCart(p);
    setCartDrawerOpen(true);
  };

  return (
    <div>
      <HeroCarousel />
      <div className="card">
        <div className="section-header">
          <h2>Top Sellers</h2>
          <a
            href="#"
            className="muted"
            onClick={(e) => {
              e.preventDefault();
              navigate("/Datailsproducts");
            }}
          >
            View All
          </a>
        </div>

        {Array.isArray(products) && products.length > 0 ? (
          <div className="top-sellers-section">
            <div className="top-sellers-grid">
              {products.slice(0, 6).map((p) => (
                <ProductCard
                  key={p._id}
                  product={p}
                  onAddToCart={onAddToCart}
                  onBuyNow={buyNow}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className="muted">No products found.</div>
        )}
      </div>
      <PromoSection />
    </div>
  );
}

function AdminDashboard() {
  const { token } = useAuth();
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // load on mount / token change
  React.useEffect(() => {
    loadStats();
  }, [token]);

  const loadStats = async () => {
    if (!token) return;
    setLoadingStats(true);
    try {
      const s = await catalogApi.getAdminStats(token);
      setStats(s || null);
    } catch (e) {
      console.error("Failed to load admin stats", e);
    } finally {
      setLoadingStats(false);
    }
  };

  return (
    <div className="card">
      <h2 className="page-title">Admin Dashboard</h2>
      <p className="muted">Overview and Statistics</p>

      {/* Stats cards */}
      <div style={{ marginTop: 24 }}>
        {loadingStats ? (
          <div>Loading stats...</div>
        ) : stats ? (
          <div
            style={{
              display: "grid",
              gap: 16,
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            }}
          >
            <div
              className="card"
              style={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                color: "white",
                padding: 24,
              }}
            >
              <h4 style={{ margin: 0, marginBottom: 8, opacity: 0.9 }}>
                Products
              </h4>
              <div style={{ fontSize: 32, fontWeight: 700 }}>
                {stats.productsCount}
              </div>
              <div style={{ opacity: 0.8, fontSize: 14 }}>Active products</div>
            </div>
            <div
              className="card"
              style={{
                background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
                color: "white",
                padding: 24,
              }}
            >
              <h4 style={{ margin: 0, marginBottom: 8, opacity: 0.9 }}>
                Categories
              </h4>
              <div style={{ fontSize: 32, fontWeight: 700 }}>
                {stats.categoriesCount}
              </div>
              <div style={{ opacity: 0.8, fontSize: 14 }}>Total categories</div>
            </div>
            <div
              className="card"
              style={{
                background: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
                color: "white",
                padding: 24,
              }}
            >
              <h4 style={{ margin: 0, marginBottom: 8, opacity: 0.9 }}>
                Orders
              </h4>
              <div style={{ fontSize: 32, fontWeight: 700 }}>
                {stats.ordersCount || 0}
              </div>
              <div style={{ opacity: 0.8, fontSize: 14 }}>
                {stats.paidOrdersCount || 0} paid,{" "}
                {stats.pendingOrdersCount || 0} pending
              </div>
            </div>
            <div
              className="card"
              style={{
                background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
                color: "white",
                padding: 24,
              }}
            >
              <h4 style={{ margin: 0, marginBottom: 8, opacity: 0.9 }}>
                Users
              </h4>
              <div style={{ fontSize: 32, fontWeight: 700 }}>
                {stats.usersCount}
              </div>
              <div style={{ opacity: 0.8, fontSize: 14 }}>
                Total users ({stats.adminsCount} admins)
              </div>
            </div>
            <div
              className="card"
              style={{
                background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
                color: "white",
                padding: 24,
              }}
            >
              <h4 style={{ margin: 0, marginBottom: 8, opacity: 0.9 }}>
                Total Revenue
              </h4>
              <div style={{ fontSize: 32, fontWeight: 700 }}>
                ₹{stats.totalRevenue?.toLocaleString() || 0}
              </div>
              <div style={{ opacity: 0.8, fontSize: 14 }}>
                All-time earnings
              </div>
            </div>
            <div
              className="card"
              style={{
                background: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
                color: "white",
                padding: 24,
              }}
            >
              <h4 style={{ margin: 0, marginBottom: 8, opacity: 0.9 }}>
                This Month
              </h4>
              <div style={{ fontSize: 32, fontWeight: 700 }}>
                ₹{stats.monthRevenue?.toLocaleString() || 0}
              </div>
              <div style={{ opacity: 0.8, fontSize: 14 }}>
                Revenue this month
              </div>
            </div>
          </div>
        ) : (
          <div className="muted">No stats available</div>
        )}
      </div>

      {/* Recent Products */}
      {stats && stats.latestProducts && stats.latestProducts.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h3>Recent Products</h3>
          <div className="card" style={{ padding: 16 }}>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {stats.latestProducts.map((p) => (
                <li
                  key={p._id}
                  style={{
                    padding: "12px 0",
                    borderBottom: "1px solid #eee",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontWeight: 500 }}>
                    {p.basic && p.basic.productName ? p.basic.productName : "—"}
                  </span>
                  <span
                    style={{
                      background: "#f0f0f0",
                      padding: "4px 12px",
                      borderRadius: 4,
                      fontWeight: 600,
                    }}
                  >
                    ₹{p.pricing && p.pricing.price ? p.pricing.price : "—"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default function App() {
  return (
    <>
      {/* Main App Providers and Router */}
      <ToastProvider>
        <AuthProvider>
          <CartProvider>
            <WishlistProvider>
              <ToastRenderer />
              <BrowserRouter>
                <TrackPageViews />
                <Suspense
                  fallback={
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100vh",
                        fontSize: "18px",
                        color: "#666",
                      }}
                    >
                      Preparing your fashion space…
                    </div>
                  }
                >
                  <Routes>
                    {/* Dashboard as Landing Page (UserOnly) with InstallPWAButton */}
                    <Route path="/" element={<UserOnlyRoute><><InstallPWAButton /><Dashboard2 /></></UserOnlyRoute>} />
                    <Route path="/dashboard" element={<UserOnlyRoute><Dashboard2 /></UserOnlyRoute>} />
                    <Route path="/dashboard2" element={<UserOnlyRoute><Dashboard2 /></UserOnlyRoute>} />
                    {/* Blog Detail Page (UserOnly) */}
                    <Route path="/blog/:slug" element={<UserOnlyRoute><BlogDetail /></UserOnlyRoute>} />
                    <Route path="/privacy-policy" element={<UserOnlyRoute><PrivacyPolicy /></UserOnlyRoute>} />
                    <Route path="/cookie-policy" element={<UserOnlyRoute><CookiePolicy /></UserOnlyRoute>} />
                    <Route
                      path="/terms-and-conditions"
                      element={<UserOnlyRoute><TermsAndConditions /></UserOnlyRoute>}
                    />
                    <Route path="/faq" element={<UserOnlyRoute><FAQ /></UserOnlyRoute>} />
                    <Route path="/blogs" element={<UserOnlyRoute><BlogDetails /></UserOnlyRoute>} />
                    {/* Admin panel routes without Layout - Protected */}
                    <Route
                      path="/admin"
                      element={
                        <AdminRoute>
                          <Adminstration />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/adminstration"
                      element={
                        <AdminRoute>
                          <Adminstration />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/track/:orderId"
                      element={<PublicOrderTracking />}
                    />
                    <Route
                      path="/admin/products"
                      element={
                        <AdminRoute>
                          <ProductsAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/products/:productId/add-locations"
                      element={
                        <AdminRoute>
                          <AddDeliveryLocations />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/orders"
                      element={
                        <AdminRoute>
                          <OrdersAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/offers"
                      element={
                        <AdminRoute>
                          <OffersAdmin />
                        </AdminRoute>
                      }
                    />
                    ;
                    <Route
                      path="/admin/categories"
                      element={
                        <AdminRoute>
                          <CategoriesAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/carousel"
                      element={
                        <AdminRoute>
                          <CarouselAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/promo"
                      element={
                        <AdminRoute>
                          <PromoAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/reels"
                      element={
                        <AdminRoute>
                          <ReelsAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/shop-the-look"
                      element={
                        <AdminRoute>
                          <ShopTheLookAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/shop-the-look-image"
                      element={
                        <AdminRoute>
                          <ShopTheLookImageAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/out-of-stock"
                      element={
                        <AdminRoute>
                          <OutOfStock />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/stock-alerts"
                      element={
                        <AdminRoute>
                          <StockAlerts />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/profile"
                      element={
                        <AdminRoute>
                          <AdminProfile />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/change-password"
                      element={
                        <AdminRoute>
                          <ChangePassword />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/customers"
                      element={
                        <AdminRoute>
                          <CustomersAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/customer-support"
                      element={
                        <AdminRoute>
                          <AdminCustomerSupport />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/order-tracking"
                      element={
                        <AdminRoute>
                          <OrderTrackingAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/order-tracking/:orderId"
                      element={
                        <AdminRoute>
                          <OrderTrackingAdmin />
                        </AdminRoute>
                      }
                    />
                    <Route path="/recommended" element={<Recommended />} />
                    <Route
                      path="/admin/activity-logs"
                      element={
                        <AdminRoute>
                          <ActivityLog />
                        </AdminRoute>
                      }
                    />
                    ;
                    <Route
                      path="/admin/notifications"
                      element={
                        <AdminRoute>
                          <Notification />
                        </AdminRoute>
                      }
                    />
                    <Route
                      path="/admin/blogs"
                      element={
                        <AdminRoute>
                          <BlogsAdmin />
                        </AdminRoute>
                      }
                    />
                    ;{/* Auth routes without Layout */}
                    <Route path="/login" element={<Login />} />
                    <Route path="/register" element={<Register />} />
                    <Route path="/verify" element={<Verify />} />
                    <Route path="/complete-profile" element={<CompleteProfile />} />
                    <Route path="/login-security" element={<LoginSecurity />} />
                    {/* Public Order Tracking - No Auth Required */}
                    {/* <Route
                    path="/track/:orderId"
                    element={<PublicOrderTracking />}
                  /> */}
                    {/* Main user-facing routes */}
                    <Route path="/products" element={<UserOnlyRoute><ProductGrid /></UserOnlyRoute>} />
                    <Route path="/wishlist" element={<UserOnlyRoute><WishlistPage /></UserOnlyRoute>} />
                    <Route path="/categories" element={<UserOnlyRoute><CategoriesPage /></UserOnlyRoute>} />
                    <Route path="/checkout" element={<UserOnlyRoute><PrivateRoute><Checkout /></PrivateRoute></UserOnlyRoute>} />
                    <Route path="/order/:id" element={<UserOnlyRoute><PrivateRoute><OrderDetails /></PrivateRoute></UserOnlyRoute>} />
                    <Route path="/product/:id" element={<UserOnlyRoute><ProductDetails /></UserOnlyRoute>} />
                    <Route path="/profile" element={<UserOnlyRoute><PrivateRoute><Profile /></PrivateRoute></UserOnlyRoute>} />
                    <Route path="/collection/:collectionName" element={<UserOnlyRoute><CategoryPage /></UserOnlyRoute>} />
                    <Route path="/customer-support" element={<UserOnlyRoute><CustomerSupport /></UserOnlyRoute>} />
                    <Route path="/recent-orders" element={<UserOnlyRoute><PrivateRoute><RecentOrders /></PrivateRoute></UserOnlyRoute>} />
                    <Route path="/Datailsproducts" element={<UserOnlyRoute><ProductListing /></UserOnlyRoute>} />
                    <Route path="/productlisting" element={<UserOnlyRoute><ProductListing /></UserOnlyRoute>} />
                    {/* Reels routes */}
                    <Route path="/reels" element={<UserOnlyRoute><ReelFeed /></UserOnlyRoute>} />
                    <Route path="/reel/:reelId" element={<UserOnlyRoute><ShareableReelPage /></UserOnlyRoute>} />
                    <Route path="/brand-reels" element={<UserOnlyRoute><PrivateRoute><BrandReelUpload /></PrivateRoute></UserOnlyRoute>} />
                    {/* 404 catch-all route */}
                    <Route path="*" element={<NotFoundPage />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </WishlistProvider>
          </CartProvider>
        </AuthProvider>
      </ToastProvider>
    </>
  );
}

// Toast Renderer Component
function ToastRenderer() {
  const { toasts, removeToast } = useToast();
  return <ToastContainer toasts={toasts} removeToast={removeToast} />;
}
